<template>
  <div
    class="min-h-screen bg-slate-50/70 pb-28 text-slate-800 font-sans antialiased"
  >
    <main
      class="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8"
    >
      <!-- Loading Skeleton -->
      <div
        v-if="isLoading"
        class="space-y-6"
      >
        <div
          class="h-44 w-full animate-pulse rounded-2xl bg-slate-200/80"
        ></div>
        <div
          class="h-96 w-full animate-pulse rounded-2xl bg-slate-200/80"
        ></div>
      </div>

      <div
        v-else
        class="space-y-6 sm:space-y-8"
      >
        <!-- 1. LIVE CAMPUS INFRASTRUCTURE PREVIEW SECTION -->
        <section class="space-y-4">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2">
              <span
                class="h-2 w-2 rounded-full bg-emerald-500 animate-pulse"
              ></span>
              <h3
                class="text-xs font-bold uppercase tracking-wider text-slate-500"
              >
                Live Infrastructure Preview
              </h3>
            </div>
            <span
              class="rounded-md bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700 shadow-xs"
            >
              Website Visual Theme Active
            </span>
          </div>

          <!-- Preview Frame Simulation matching Website Render -->
          <div
            class="rounded-2xl border border-slate-900 bg-[#11151F] text-white p-5 sm:p-8 shadow-xl overflow-hidden relative"
          >
            <div class="max-w-2xl mb-6">
              <span
                class="inline-flex items-center gap-2 font-mono text-[10px] font-bold tracking-widest text-[#D8B372] uppercase"
              >
                Take a look inside
              </span>
              <h2
                class="text-xl sm:text-2xl font-bold font-serif text-white tracking-tight mt-1"
              >
                Campus Infrastructure
              </h2>
              <p class="text-xs sm:text-sm text-[#C9C3B2] mt-1 leading-relaxed">
                Every corner of campus is built for one thing — getting students
                out of their seats and into the work.
              </p>
            </div>

            <!-- Tab Switcher Bar -->
            <div class="flex flex-wrap gap-2 mb-6">
              <button
                v-for="(tab, pIdx) in activeTabs"
                :key="tab._id || pIdx"
                type="button"
                @click="previewActiveTab = pIdx"
                class="flex items-center gap-2 px-3.5 py-1.5 rounded text-xs font-semibold uppercase tracking-wider transition-all duration-200 border cursor-pointer"
                :class="[
                  previewActiveTab === pIdx
                    ? 'bg-[#B8863B] text-[#11151F] border-[#B8863B] font-bold shadow-md'
                    : 'bg-transparent text-[#C9C3B2] border-white/20 hover:border-[#B8863B]',
                ]"
              >
                <span class="font-mono text-[10px] opacity-80">
                  {{ pIdx + 1 < 10 ? "0" + (pIdx + 1) : pIdx + 1 }}
                </span>
                <span>{{ tab.title || "Untitled Tab" }}</span>
              </button>

              <span
                v-if="activeTabs.length === 0"
                class="text-xs text-white/40 italic py-1"
              >
                No active infrastructure tabs available.
              </span>
            </div>

            <!-- Active Visual Display -->
            <div
              class="relative w-full rounded-xl overflow-hidden border border-[#B8863B]/30 bg-slate-950 aspect-video md:aspect-[16/8]"
            >
              <img
                v-if="selectedPreviewImage"
                :src="selectedPreviewImage"
                :alt="
                  activeTabs[previewActiveTab]?.title ||
                  'Campus Facility Preview'
                "
                class="w-full h-full object-cover transition-opacity duration-300"
              />
              <div
                v-else
                class="w-full h-full flex flex-col items-center justify-center text-slate-400 bg-slate-900/60 p-4 text-center"
              >
                <ImageIcon class="h-10 w-10 text-slate-500 mb-2" />
                <span
                  class="text-xs uppercase font-mono font-bold text-slate-400"
                  >No Image Uploaded for this Tab</span
                >
              </div>

              <!-- Overlay Label Chip -->
              <div
                v-if="activeTabs[previewActiveTab]"
                class="absolute bottom-3 left-3 bg-[#FAF7F1] text-[#11151F] font-mono text-[10px] font-bold px-3 py-1 rounded shadow flex items-center gap-2"
              >
                <span class="h-1.5 w-1.5 rounded-full bg-[#B8863B]"></span>
                <span>{{
                  activeTabs[previewActiveTab]?.title || "Overview"
                }}</span>
              </div>
            </div>
          </div>
        </section>

        <!-- 2. STATS OVERVIEW CARDS -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs flex items-center gap-4"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 font-bold shrink-0"
            >
              <Building2Icon class="h-6 w-6" />
            </div>
            <div>
              <p
                class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
              >
                Total Facilities
              </p>
              <h4 class="text-xl font-bold text-slate-900">
                {{ infraTabs.length }}
              </h4>
            </div>
          </div>

          <div
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs flex items-center gap-4"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 font-bold shrink-0"
            >
              <CheckCircleIcon class="h-6 w-6" />
            </div>
            <div>
              <p
                class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
              >
                Active Published
              </p>
              <h4 class="text-xl font-bold text-slate-900">
                {{ activeTabs.length }}
              </h4>
            </div>
          </div>

          <div
            class="rounded-2xl border border-slate-200 bg-white p-4 shadow-xs flex items-center gap-4"
          >
            <div
              class="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-600 font-bold shrink-0"
            >
              <ImageIcon class="h-6 w-6" />
            </div>
            <div>
              <p
                class="text-xs font-semibold text-slate-500 uppercase tracking-wider"
              >
                Photos Configured
              </p>
              <h4 class="text-xl font-bold text-slate-900">
                {{ configuredPhotosCount }}
              </h4>
            </div>
          </div>
        </div>

        <!-- 3. INFRASTRUCTURE TABS MANAGER -->
        <section
          class="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-6 shadow-xs space-y-4"
        >
          <div
            class="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 pb-4 gap-3"
          >
            <div>
              <div class="flex items-center space-x-2.5">
                <ListFilterIcon class="h-5 w-5 text-indigo-600" />
                <h2 class="text-base font-bold text-slate-900">
                  Infrastructure Tabs Directory ({{ infraTabs.length }})
                </h2>
              </div>
              <p class="text-xs text-slate-500 mt-0.5">
                Manage section titles, upload facility photography, and control
                tab order
              </p>
            </div>

            <button
              type="button"
              class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-xs font-bold text-white transition hover:bg-indigo-500 active:scale-95 cursor-pointer shadow-md shadow-indigo-600/20"
              @click="addTab"
            >
              <PlusIcon class="h-4 w-4" />
              <span>New Infrastructure Tab</span>
            </button>
          </div>

          <!-- TAB LIST ITEMS -->
          <div class="space-y-4">
            <div
              v-for="(tab, index) in infraTabs"
              :key="tab._id || index"
              class="rounded-xl border transition-all duration-200 p-3.5 sm:p-4 space-y-4"
              :class="[
                tab.active !== false
                  ? 'border-slate-200 bg-slate-50/50 shadow-xs'
                  : 'border-slate-200/60 bg-slate-100/50 opacity-60',
              ]"
            >
              <!-- TOP CONTROL STRIP -->
              <div
                class="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/60 pb-2.5"
              >
                <div class="flex items-center gap-2">
                  <span class="text-xs font-mono font-bold text-slate-400"
                    >#{{ index + 1 }}</span
                  >
                  <span
                    class="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
                    :class="
                      tab.active !== false
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-slate-200 text-slate-600'
                    "
                  >
                    {{ tab.active !== false ? "Visible on Site" : "Hidden" }}
                  </span>
                </div>

                <!-- ACTIONS & ORDER -->
                <div class="flex items-center gap-3">
                  <label
                    class="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 cursor-pointer"
                  >
                    <span>Active:</span>
                    <button
                      type="button"
                      class="relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                      :class="
                        tab.active !== false ? 'bg-emerald-600' : 'bg-slate-300'
                      "
                      @click="tab.active = tab.active === false"
                    >
                      <span
                        class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out"
                        :class="
                          tab.active !== false
                            ? 'translate-x-4'
                            : 'translate-x-0'
                        "
                      ></span>
                    </button>
                  </label>

                  <div class="flex items-center gap-1 ml-2">
                    <button
                      type="button"
                      :disabled="index === 0"
                      class="p-1 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 disabled:opacity-20 cursor-pointer active:scale-95"
                      @click="moveTab(index, -1)"
                    >
                      <ArrowUpIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      :disabled="index === infraTabs.length - 1"
                      class="p-1 rounded-md border border-slate-200 bg-white text-slate-500 hover:text-indigo-600 disabled:opacity-20 cursor-pointer active:scale-95"
                      @click="moveTab(index, 1)"
                    >
                      <ArrowDownIcon class="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      class="p-1 rounded-md border border-slate-200 bg-white text-rose-500 hover:bg-rose-50 cursor-pointer active:scale-95"
                      @click="removeTab(index)"
                    >
                      <Trash2Icon class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- FORM CONTENT & MEDIA -->
              <div class="flex flex-col md:flex-row gap-4 items-start">
                <!-- IMAGE PREVIEW & UPLOADER -->
                <div class="w-full md:w-56 shrink-0 space-y-1.5">
                  <label
                    class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider"
                  >
                    Facility Photograph
                  </label>

                  <div
                    class="relative group rounded-xl border-2 border-dashed border-slate-300 overflow-hidden bg-white h-32 w-full flex flex-col items-center justify-center p-2 text-center transition hover:border-indigo-500"
                    style="aspect-ratio: 16 / 9"
                  >
                    <img
                      v-if="getImagePreview(tab)"
                      :src="getImagePreview(tab)"
                      :alt="tab.title"
                      class="absolute inset-0 w-full h-full object-cover object-center"
                    />
                    <div
                      v-else
                      class="space-y-1 text-slate-400 p-2"
                    >
                      <ImageIcon class="mx-auto h-6 w-6" />
                      <span class="block text-[10px] font-semibold uppercase"
                        >Upload Image</span
                      >
                    </div>

                    <input
                      type="file"
                      accept="image/*"
                      class="absolute inset-0 opacity-0 cursor-pointer z-10"
                      @change="(e) => handleImageSelect(e, tab)"
                    />

                    <button
                      v-if="getImagePreview(tab)"
                      type="button"
                      class="absolute top-1.5 right-1.5 z-20 p-1 rounded-full bg-slate-900/80 text-white hover:bg-rose-600 transition"
                      @click.stop="removeImage(tab)"
                    >
                      <XIcon class="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>

                <!-- MAIN FORM FIELDS -->
                <div class="w-full flex-1 space-y-3">
                  <div>
                    <label
                      class="block text-[11px] font-semibold text-slate-600 uppercase tracking-wider mb-1"
                    >
                      Tab Title / Facility Name
                    </label>
                    <input
                      v-model="tab.title"
                      type="text"
                      placeholder="e.g. Science Labs, Transport, Athletic Fields"
                      class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 focus:border-indigo-500 focus:outline-none"
                    />
                  </div>

                  <div
                    class="rounded-lg bg-slate-100/80 p-3 text-[11px] text-slate-500 flex items-center gap-2"
                  >
                    <CheckCircleIcon class="h-4 w-4 text-slate-400 shrink-0" />
                    <span
                      >This title renders as button #{{
                        index + 1 < 10 ? "0" + (index + 1) : index + 1
                      }}
                      in the interactive switcher.</span
                    >
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="infraTabs.length === 0"
              class="p-8 text-center border-2 border-dashed border-slate-200 rounded-xl"
            >
              <Building2Icon class="mx-auto h-8 w-8 text-slate-400" />
              <p class="mt-2 text-xs font-medium text-slate-500">
                No infrastructure tabs created yet.
              </p>
              <button
                type="button"
                class="mt-3 inline-flex items-center text-xs font-bold text-indigo-600 hover:text-indigo-500 cursor-pointer"
                @click="addTab"
              >
                + Add First Tab
              </button>
            </div>
          </div>
        </section>
      </div>

      <!-- COMPACT FLOATING ACTION BAR -->
      <div
        class="fixed bottom-4 right-3 sm:right-6 lg:right-8 z-40 max-w-[calc(100vw-1.5rem)] sm:max-w-lg"
      >
        <div
          class="rounded-2xl bg-white/95 backdrop-blur-xl border border-slate-200/90 p-2.5 sm:p-3 shadow-xl shadow-slate-900/10 flex items-center justify-between gap-3 sm:gap-4 ring-1 ring-slate-950/5"
        >
          <!-- Status Indicator -->
          <div class="flex items-center gap-2 text-xs font-medium min-w-0">
            <span
              v-if="isSaving"
              class="inline-flex items-center gap-1.5 text-indigo-600 font-semibold truncate"
            >
              <Loader2Icon
                class="h-4 w-4 animate-spin shrink-0 text-indigo-600"
              />
              <span class="truncate">Saving...</span>
            </span>

            <span
              v-else-if="hasUnsavedChanges"
              class="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 border border-amber-200/80 text-amber-700 font-bold shrink-0"
            >
              <span
                class="h-2 w-2 rounded-full bg-amber-500 animate-pulse"
              ></span>
              <span>Unsaved</span>
            </span>

            <span
              v-else
              class="inline-flex items-center gap-1.5 text-slate-500 truncate"
            >
              <CheckCircleIcon class="h-4 w-4 text-emerald-500 shrink-0" />
              <span class="hidden sm:inline text-xs truncate"
                >All changes saved</span
              >
            </span>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center gap-2 shrink-0">
            <button
              type="button"
              class="rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-bold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition active:scale-95 cursor-pointer"
              :disabled="isSaving"
              @click="confirmReset"
            >
              Discard
            </button>

            <button
              type="button"
              class="inline-flex items-center justify-center space-x-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 active:scale-95 px-4 sm:px-5 py-2 text-xs font-bold text-white shadow-md shadow-indigo-600/30 transition-all duration-150 disabled:opacity-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
              :disabled="isSaving"
              @click="handleSave"
            >
              <Loader2Icon
                v-if="isSaving"
                class="h-3.5 w-3.5 animate-spin"
              />
              <SaveIcon
                v-else
                class="h-3.5 w-3.5"
              />
              <span class="whitespace-nowrap">{{
                isSaving ? "Saving" : "Save Infrastructure"
              }}</span>
            </button>
          </div>
        </div>
      </div>
    </main>

    <!-- TOAST NOTIFICATION -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-24 right-4 sm:right-6 z-50 flex items-center space-x-2.5 rounded-2xl px-4 py-3 text-xs font-bold text-white shadow-2xl"
        :class="toast.type === 'success' ? 'bg-emerald-600' : 'bg-rose-600'"
      >
        <CheckCircleIcon
          v-if="toast.type === 'success'"
          class="h-4 w-4"
        />
        <AlertCircleIcon
          v-else
          class="h-4 w-4"
        />
        <span>{{ toast.message }}</span>
      </div>
    </Transition>

    <!-- RESET CONFIRMATION MODAL -->
    <div
      v-if="showResetModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
    >
      <div
        class="w-full max-w-md space-y-4 overflow-hidden rounded-2xl bg-white p-5 sm:p-6 shadow-2xl"
      >
        <h3 class="text-base font-bold text-slate-900">
          Discard Unsaved Changes?
        </h3>
        <p class="text-xs text-slate-500 leading-relaxed">
          Are you sure you want to reset all campus infrastructure
          configurations back to their saved state?
        </p>
        <div class="flex justify-end space-x-2.5 pt-2">
          <button
            type="button"
            class="rounded-xl border border-slate-200 px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 active:scale-95 cursor-pointer"
            @click="showResetModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-xl bg-rose-600 px-4 py-2 text-xs font-bold text-white shadow-md shadow-rose-600/20 hover:bg-rose-500 active:scale-95 cursor-pointer"
            @click="executeReset"
          >
            Yes, Discard
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, reactive, computed, onMounted, onBeforeUnmount } from "vue";
  import { useStore } from "vuex";
  import {
    Plus as PlusIcon,
    Save as SaveIcon,
    Loader2 as Loader2Icon,
    CheckCircle as CheckCircleIcon,
    AlertCircle as AlertCircleIcon,
    Trash2 as Trash2Icon,
    ArrowUp as ArrowUpIcon,
    ArrowDown as ArrowDownIcon,
    ListFilter as ListFilterIcon,
    Image as ImageIcon,
    Building2 as Building2Icon,
    X as XIcon,
  } from "lucide-vue-next";

  const store = useStore();

  const isLoading = ref(true);
  const isSaving = ref(false);
  const showResetModal = ref(false);
  const previewActiveTab = ref(0);

  const infraTabs = ref([]);
  const initialStateJson = ref("");

  const toast = reactive({
    show: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type = "success") => {
    toast.show = true;
    toast.message = message;
    toast.type = type;
    setTimeout(() => {
      toast.show = false;
    }, 4000);
  };

  const activeTabs = computed(() =>
    infraTabs.value.filter((t) => t.active !== false),
  );

  const configuredPhotosCount = computed(
    () =>
      infraTabs.value.filter(
        (t) =>
          t.previewUrl || (typeof t.image === "string" && t.image.length > 0),
      ).length,
  );

  const selectedPreviewImage = computed(() => {
    if (!activeTabs.value.length) return "";
    const currentTab =
      activeTabs.value[previewActiveTab.value] || activeTabs.value[0];
    return getImagePreview(currentTab);
  });

  function getCleanTabState(tab) {
    return {
      _id: tab._id,
      title: tab.title || "",
      active: tab.active !== false,
      image: typeof tab.image === "string" ? tab.image : "",
      hasNewFile: tab.imageFile instanceof File,
      fileName: tab.imageFile instanceof File ? tab.imageFile.name : "",
      fileSize: tab.imageFile instanceof File ? tab.imageFile.size : 0,
    };
  }

  const hasUnsavedChanges = computed(() => {
    const currentClean = infraTabs.value.map(getCleanTabState);
    return JSON.stringify(currentClean) !== initialStateJson.value;
  });

  function snapshotState() {
    const cleanSnapshot = infraTabs.value.map(getCleanTabState);
    initialStateJson.value = JSON.stringify(cleanSnapshot);
  }

  function addTab() {
    infraTabs.value.push({
      title: "New Facility",
      image: "",
      imageFile: null,
      previewUrl: "",
      active: true,
    });
  }

  function removeTab(index) {
    infraTabs.value.splice(index, 1);
    if (previewActiveTab.value >= infraTabs.value.length) {
      previewActiveTab.value = Math.max(0, infraTabs.value.length - 1);
    }
  }

  function moveTab(index, direction) {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= infraTabs.value.length) return;
    const item = infraTabs.value.splice(index, 1)[0];
    infraTabs.value.splice(targetIndex, 0, item);
  }

  function handleImageSelect(event, tab) {
    const file = event.target.files[0];
    if (!file) return;
    tab.imageFile = file;
    tab.previewUrl = URL.createObjectURL(file);
  }

  function removeImage(tab) {
    tab.image = "";
    tab.imageFile = null;
    tab.previewUrl = "";
  }

  function getImagePreview(tab) {
    if (!tab) return "";
    return tab.previewUrl || tab.image || "";
  }

  const loadData = async () => {
    isLoading.value = true;
    try {
      await store.dispatch("fetchWebsiteDetails");
      const details = store.state.websiteDetails || {};

      if (
        Array.isArray(details.infrastructureTabs) &&
        details.infrastructureTabs.length > 0
      ) {
        infraTabs.value = details.infrastructureTabs.map((t) => ({
          _id: t._id,
          title: t.title || "",
          image: t.image || "",
          imageFile: null,
          previewUrl: "",
          active: t.active !== false,
        }));
      } else {
        infraTabs.value = [
          {
            title: "Overview",
            image:
              "https://dpstezpur.com/wp-content/uploads/elementor/thumbs/Classroom1-qqydxco77xhy2vmk97e1qpbnb7y3o3f6ym76f7x4kc.jpg",
            imageFile: null,
            previewUrl: "",
            active: true,
          },
          {
            title: "Classrooms",
            image:
              "https://dpstezpur.com/wp-content/uploads/elementor/thumbs/Classroom1-qqydxco77xhy2vmk97e1qpbnb7y3o3f6ym76f7x4kc.jpg",
            imageFile: null,
            previewUrl: "",
            active: true,
          },
          {
            title: "Transport",
            image:
              "https://dpstezpur.com/wp-content/uploads/elementor/thumbs/Bus-Service-Hover-qqydxbqdn0uvk9qsm5f088zd8i6js9xbkdua0n74r4.jpg",
            imageFile: null,
            previewUrl: "",
            active: true,
          },
        ];
      }

      snapshotState();
    } catch (err) {
      showToast("Failed to load infrastructure settings", "error");
    } finally {
      isLoading.value = false;
    }
  };

  const handleSave = async () => {
    isSaving.value = true;
    try {
      const payload = {
        infrastructureTabs: infraTabs.value.map((t) => ({
          _id: t._id,
          title: t.title || "",
          active: t.active !== false,
          image: typeof t.image === "string" ? t.image : "",
          imageFile: t.imageFile || null,
        })),
      };

      await store.dispatch("updateWebsiteDetails", payload);
      await loadData();
      showToast("Campus infrastructure saved successfully!", "success");
    } catch (err) {
      showToast(err.message || "Error saving infrastructure details", "error");
    } finally {
      isSaving.value = false;
    }
  };

  const confirmReset = () => {
    if (hasUnsavedChanges.value) {
      showResetModal.value = true;
    } else {
      executeReset();
    }
  };

  const executeReset = () => {
    showResetModal.value = false;
    loadData();
    showToast("Infrastructure reset to last saved state", "success");
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === "s") {
      e.preventDefault();
      handleSave();
    }
  };

  onMounted(async () => {
    await loadData();
    window.addEventListener("keydown", handleKeyDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener("keydown", handleKeyDown);
  });
</script>

<style scoped>
  .toast-enter-active,
  .toast-leave-active {
    transition: all 0.3s ease;
  }
  .toast-enter-from,
  .toast-leave-to {
    opacity: 0;
    transform: translateY(1rem);
  }
</style>
